import React, { useMemo } from "react";
import { Table, Button, Popconfirm, Skeleton, Space, Tooltip, Tag } from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  FileTextOutlined,
  UserOutlined,
  MailOutlined,
} from "@ant-design/icons";

const ContestantsTableComponent = ({
  loading,
  contestants,
  handleRowClick,
  handleContestantApplicationsViewClick,
  handleContestantViewClick,
  handleDelete,
  handleApplyToEvents,
  handlePassContestant,
  handleResendEligibility,
  pagination,
  settings,
  userRole,
  handleSelectProvince
}) => {
  const columns = useMemo(() => {
    return [
      {
        title: "Round",
        dataIndex: "round",
        key: "round",
        render: (text, record) => {
          let color;
          let statusText;

          if (record.round === 1) {
            color = "blue";
            statusText = "Round 1";
          } else if (record.round === 2) {
            color = "green";
            statusText = "Round 2";
          } else if (record.round === 3) {
            color = "purple";
            statusText = "Round 3";
          } else {
            color = "default";
            statusText = "-";
          }
          return <Tag color={color}>{statusText}</Tag>;
        },
      },
      {
        title: "Contestant Number",
        dataIndex: "contestant_number",
        key: "contestant_number",
      },
      {
        title: "Full Name",
        key: "full_name",
        render: (text, record) =>
          `${record.first_name || ""} ${record.last_name || ""}`.trim(),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Invoice Status",
        key: "invoiceStatus",
        render: (text, record) => {
          const invoiceForRound = record.invoices?.find(
            (inv) => inv.round === record.round
          );
          let color;
          let statusText;

          if (!invoiceForRound) {
            color = "blue";
            statusText = "No Invoice";
          } else if (invoiceForRound.paid) {
            color = "green";
            statusText = "Paid";
          } else {
            color = "red";
            statusText = "Not Paid";
          }
          return <Tag color={color}>{statusText}</Tag>;
        },
      },
      {
        title: "Actions",
        key: "actions",
        render: (text, record) => {
          // If the contestant has no province, show Select Province only for non-admins,
          // and for admins show Select Province + Info
          if (!record.province) {
            if (userRole === "admin" || userRole === "guardian") {
              return (
                <Space>
                  <Button
                    type="primary"
                    onClick={() => handleSelectProvince(record)}
                  >
                    Select Province
                  </Button>

                  <Button
                    onClick={() => handleContestantViewClick(record)}
                    icon={<UserOutlined />}
                  >
                    Info
                  </Button>
                </Space>
              );
            }

            return (
              <Button
                type="primary"
                onClick={() => handleSelectProvince(record)}
              >
                Select Province
              </Button>
            );
          }

          // Existing actions if province is present
          return (
            <Space>
              <Button
                onClick={() => handleContestantViewClick(record)}
                icon={<UserOutlined />}
              >
                Info
              </Button>

              <Button
                onClick={() => handleContestantApplicationsViewClick(record)}
                icon={<CalendarOutlined />}
              >
                Events
              </Button>

              {!(record.round === 2 && !settings.nationalApplicationsOpen) && (
                <Button
                  onClick={() => handleRowClick(record._id)}
                  icon={<FileTextOutlined />}
                >
                  Pay Here
                </Button>
              )}

              {!(record.round === 2 && !settings.nationalApplicationsOpen) && (
                <Button
                  type="primary"
                  onClick={() => handleApplyToEvents(record)}
                >
                  Apply to Events
                </Button>
              )}

              {record.round === settings.selectedRound && userRole === "admin" ? (
                <Button
                  type="primary"
                  onClick={() => handlePassContestant(record)}
                >
                  Pass Contestant
                </Button>
              ) : null}

              {userRole === "admin" && record.round === 2 && (
                <Button
                  onClick={() => handleResendEligibility(record)}
                  icon={<MailOutlined />}
                >
                  Resend Email
                </Button>
              )}

              <Popconfirm
                title="Are you sure you want to delete this contestant?"
                onConfirm={() => handleDelete(record._id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete Contestant">
                  <Button
                    danger
                    type="text"
                    shape="circle"
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          );
        },
      },
    ];
  }, [
    handleApplyToEvents,
    handleContestantApplicationsViewClick,
    handleContestantViewClick,
    handleDelete,
    handlePassContestant,
    handleResendEligibility,
    handleRowClick,
    settings.nationalApplicationsOpen,
    settings.selectedRound,
    userRole,
    handleSelectProvince
  ]);

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <Table
      columns={columns}
      dataSource={contestants}
      rowKey={(record) => record._id || record.key}
      pagination={pagination}
      scroll={{ x: true }}
    />
  );
};

const ContestantsTable = React.memo(ContestantsTableComponent);

export default ContestantsTable;
